import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MousePointerClick, SlidersHorizontal, X } from "lucide-react";
import { useLang } from "../i18n";
import { Header } from "../components/Header";
import { MapView } from "../components/MapView";
import { StoryPanel } from "../components/StoryPanel";
import { ContentWarning } from "../components/ContentWarning";
import { SymbolIcon, SYMBOL_KEYS } from "../components/SymbolIcon";
import api from "../lib/api";

const CATEGORIES = ["domestic", "sexual", "harassment", "psychological", "other"];

export default function MapPage() {
  const { t, lang } = useLang();
  const [accepted, setAccepted] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [stories, setStories] = useState([]);
  const [tempPoint, setTempPoint] = useState(null);
  const [symbolFilter, setSymbolFilter] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [recentOnly, setRecentOnly] = useState(false);

  const fetchStories = useCallback(async () => {
    const params = {};
    if (symbolFilter) params.symbol = symbolFilter;
    if (categoryFilter) params.category = categoryFilter;
    try {
      const { data } = await api.get("/stories", { params });
      setStories(data);
    } catch (e) {
      setStories([]);
    }
  }, [symbolFilter, categoryFilter]);

  useEffect(() => {
    fetchStories();
  }, [fetchStories]);

  const visible = useMemo(() => {
    if (!recentOnly) return stories;
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    return stories.filter((s) => new Date(s.created_at).getTime() >= cutoff);
  }, [stories, recentOnly]);

  const clearFilters = () => {
    setSymbolFilter(null);
    setCategoryFilter(null);
    setRecentOnly(false);
  };

  const handleEnter = () => {
    setAccepted(true);
  };

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-softpink">
      <ContentWarning open={!accepted} onEnter={handleEnter} />
      <Header />

      {/* Map */}
      <div className="absolute inset-0">
        <MapView stories={visible} tempPoint={tempPoint} lang={lang} onMapClick={(ll) => setTempPoint(ll)} />
      </div>

      {/* Back + title */}
      <div className="pointer-events-none absolute left-0 right-0 top-20 z-[600] flex justify-center px-5">
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="pointer-events-auto flex items-center gap-2 rounded-full bg-white/90 px-4 py-2 text-xs text-textsec shadow-soft backdrop-blur"
        >
          <MousePointerClick size={15} strokeWidth={1.75} className="text-dusty" />
          {t("map_hint")}
        </motion.div>
      </div>

      {/* Mobile filters toggle */}
      <button
        data-testid="mobile-filters-toggle"
        onClick={() => setFiltersOpen(true)}
        className="absolute left-5 top-32 z-[600] inline-flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 text-sm font-medium text-graphite shadow-soft backdrop-blur sm:hidden"
      >
        <SlidersHorizontal size={16} strokeWidth={1.75} className="text-dusty" />
        {t("filters")}
        {(symbolFilter || categoryFilter || recentOnly) && (
          <span className="h-2 w-2 rounded-full bg-dusty" />
        )}
      </button>

      {/* Mobile backdrop */}
      {filtersOpen && (
        <div
          data-testid="mobile-filters-backdrop"
          onClick={() => setFiltersOpen(false)}
          className="fixed inset-0 z-[650] bg-graphite/30 backdrop-blur-sm sm:hidden"
        />
      )}

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, x: -16 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        data-testid="filters-panel"
        className={`${
          filtersOpen ? "flex" : "hidden"
        } fixed inset-x-3 bottom-3 top-24 z-[700] flex-col overflow-y-auto rounded-panel bg-white/95 p-5 shadow-soft backdrop-blur sm:absolute sm:inset-x-auto sm:bottom-auto sm:left-5 sm:top-32 sm:flex sm:w-64 sm:overflow-visible`}
      >
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-graphite">{t("filters")}</h3>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="map-home-link" className="inline-flex items-center gap-1 text-xs text-textsec hover:text-graphite">
              <ArrowLeft size={13} strokeWidth={1.75} />
              {t("back_home")}
            </Link>
            <button
              data-testid="mobile-filters-close"
              onClick={() => setFiltersOpen(false)}
              className="flex h-7 w-7 items-center justify-center rounded-full bg-softpink text-textsec sm:hidden"
            >
              <X size={15} strokeWidth={1.75} />
            </button>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            data-testid="filter-all"
            onClick={clearFilters}
            className={`flex-1 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
              !symbolFilter && !categoryFilter && !recentOnly ? "bg-dusty text-white" : "bg-softpink text-textsec"
            }`}
          >
            {t("f_all")}
          </button>
          <button
            data-testid="filter-recent"
            onClick={() => setRecentOnly((v) => !v)}
            className={`flex-1 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
              recentOnly ? "bg-dusty text-white" : "bg-softpink text-textsec"
            }`}
          >
            {t("f_recent")}
          </button>
        </div>

        <p className="mt-4 mb-2 text-xs font-medium uppercase tracking-wider text-textsec">{t("by_symbol")}</p>
        <div className="grid grid-cols-7 gap-1">
          {SYMBOL_KEYS.map((s) => (
            <button
              key={s}
              data-testid={`filter-symbol-${s}`}
              title={t(`sym_${s}`)}
              onClick={() => setSymbolFilter((cur) => (cur === s ? null : s))}
              className={`flex aspect-square items-center justify-center rounded-lg transition-colors ${
                symbolFilter === s ? "bg-dusty text-white" : "text-dusty hover:bg-softpink"
              }`}
            >
              <SymbolIcon name={s} size={16} />
            </button>
          ))}
        </div>

        <p className="mt-4 mb-2 text-xs font-medium uppercase tracking-wider text-textsec">{t("by_category")}</p>
        <div className="flex flex-wrap gap-1.5">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`filter-category-${c}`}
              onClick={() => setCategoryFilter((cur) => (cur === c ? null : c))}
              className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                categoryFilter === c ? "bg-dusty text-white" : "bg-softpink text-textsec hover:text-graphite"
              }`}
            >
              {t(`cat_${c}`)}
            </button>
          ))}
        </div>

        {(symbolFilter || categoryFilter || recentOnly) && (
          <button
            data-testid="filter-clear"
            onClick={clearFilters}
            className="mt-4 w-full rounded-btn border border-softpink py-1.5 text-xs font-medium text-textsec hover:text-graphite"
          >
            {t("clear")}
          </button>
        )}
      </motion.div>

      {/* Side panel */}
      <StoryPanel
        point={tempPoint}
        onClose={() => setTempPoint(null)}
        onSubmitted={() => {
          setTempPoint(null);
          fetchStories();
        }}
      />
    </div>
  );
}
